import { makeAutoObservable, runInAction } from 'mobx';
import { IResolution, RESOLUTION_CATEGORY } from '../../../models/ResolutionTypes';
import ResolutionsService, { IResolutionSettings } from '../../../services/api/resolutions-service';
import { rootContainer, TYPE } from '../../../services/container/root';
import { uuid } from '../../../services/helpers';
import portalStore from '../../../stores/current-portal-store';
import { CREDIT_AMOUNT_TYPE, IMAGE_OPTION, RESOLUTION_ID, RESOLUTION_TYPE } from '../constants';
import ResolutionStore from './resolution-store';
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../models/ProfileCapabilities";


export class ResolutionsCategoryStore {
    public isExpanded?: boolean;

    public get filteredList(): ResolutionStore[] {
        return this.list.filter((r) => {
			if (r.deleted) {
				return false;
			}

			if (!portalStore.platform?.isResolutionSupportedById(r.resolutionId)) {
				// TODO: replace with capability checks
				return false;
			}

			if (r.isReturnScore()) {
				const instantCreditResolutionsCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.INSTANT_CREDIT_RESOLUTION);
				if (!instantCreditResolutionsCapability.isSupported || !instantCreditResolutionsCapability.isFeatureEnabled) {
					return false;
				}

				switch (r.resolutionId) {
					case RESOLUTION_ID.KEEP:
						return instantCreditResolutionsCapability.isKeepItemResolutionSupported;
					case RESOLUTION_ID.DONATE:
						return instantCreditResolutionsCapability.isDonateItemResolutionSupported;
					case RESOLUTION_ID.SHIP_LATER:
						return instantCreditResolutionsCapability.isShipLaterResolutionSupported;
				}
			}

			return true;
        });
    }

    constructor(public category: RESOLUTION_CATEGORY, public list: ResolutionStore[]) {
        this.isExpanded = false;
        makeAutoObservable(this);
    }
}

class ResolutionListStore {
    resolutions: ResolutionsCategoryStore[] = [];
    settings: IResolutionSettings = {} as IResolutionSettings;

    private _selectedResolutionId?: number|string;
    private _isLoading: boolean = false;

    private get resolutionsService(): ResolutionsService {
		return rootContainer.get<ResolutionsService>(TYPE.ResolutionsService);
	}

    public get selectedResolution(): ResolutionStore | undefined {
        return this.resolutions.find(c => 
            c.list.find(r => 
                r.isIdEquals(this._selectedResolutionId)
            )
        )?.list.find(r => 
            r.isIdEquals(this._selectedResolutionId
        ));
    }

    public get allowOutOfStockExchangeOptions() {
        return this.settings.allow_out_of_stock_exchange_options;
    }

    public setSelectedResolutionId(resolutionId: number|string) {
        this._selectedResolutionId = resolutionId;
    }

    constructor() {
        this._discardResolution = this._discardResolution.bind(this);
        this.toggleAllExpanded = this.toggleAllExpanded.bind(this);
		makeAutoObservable(this);
	}

    public isAllExpanded(): boolean {
        return this.resolutions.every(cat => cat.isExpanded);
    }

    public toggleAllExpanded(): void {
        const isAllExpaned = this.isAllExpanded();
        this.resolutions.forEach(cat => cat.isExpanded = !isAllExpaned);
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    public getRefundByOptions = () => {
        return [...this.settings.exchange_refund_by_list, ...this.settings.exchange_refund_by_providers];
    }

    async load() {
        this._isLoading = true;
        const [resolutionsData] = await Promise.all([this.resolutionsService.getResolutionsList(), portalStore.load()]);
		runInAction(() => {
            this._isLoading = false;

            this.resolutions = resolutionsData.resolutions.map(resolutionCategory => {
                return new ResolutionsCategoryStore(
                    resolutionCategory.category,
                    resolutionCategory.list.map(resolution => 
                        new ResolutionStore(this._discardResolution, resolution, resolutionsData.settings)
                    )
                );
            });

            this.settings = resolutionsData.settings;
		});
	}

    public addResolution(resolutionCategory: RESOLUTION_CATEGORY): string {
        const clientId = uuid();
        const suffix = '_#' + uuid().split('-')[4];

        const newResolution = new ResolutionStore(this._discardResolution, {} as IResolution, this.settings);
        const resolutionCommonFields = {
            _client_id: clientId,
            name: 'GENERIC_RESOLUTION' + suffix,
            description: 'GENERIC_RESOLUTION_DESCRIPTION' + suffix,
            resolution_type: RESOLUTION_TYPE.Credits,
            is_return_go: false,
        };
        
        switch (resolutionCategory) {
            case RESOLUTION_CATEGORY.exchange_resolutions: 
                newResolution.setChanges({
                    ...resolutionCommonFields,
                    ...{
                        resolution_id: RESOLUTION_ID.MANUAL_EXCHANGE,
                        resolution_type: RESOLUTION_TYPE.ManualExchangeType,
                        image: IMAGE_OPTION.OPTIONAL,
                    }
                });
                break;
            case RESOLUTION_CATEGORY.standard_resolutions:
                newResolution.setChanges({
                    ...resolutionCommonFields,
                    ...{
                        resolution_id: RESOLUTION_ID.REFUND_TO_CREDIT,
                        modify_credit_amount: 0,
                        credit_amount_type: CREDIT_AMOUNT_TYPE.MORE,
                        is_percentage: true,
                    }
                });
                break;
            default: 
                throw new Error('addResolution trying to add new resolution of not supported type');
        }

        const resolutionsList = this.resolutions.find(c => c.category === resolutionCategory);
        if (!resolutionsList) {
            /** should never happen */
            throw new Error(`addResolution: did not find the list store for category ${resolutionCategory}`);
        }
        resolutionsList.list.push(newResolution);

        return newResolution.id as string;
    }

    /** removes newly created reason store after it was discarded */
    private _discardResolution(store: ResolutionStore): void {
        let resoutionFound = false;
        this.resolutions.every(c => {
            const storeIndex = c.list.findIndex(r => r === store);

            if (storeIndex >= 0) {
                c.list.splice(storeIndex, 1);
                resoutionFound = true;
                return false;
            }

            return true;
        })

        if (!resoutionFound) {
            /** should never happen */
            throw new Error('discardResolution did not find the store to remove');
        }
    }

    public deleteResolution(store: ResolutionStore) {
        this._discardResolution(store);
    }
}

export default new ResolutionListStore();